import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QuestionOut, QuestionsListResponse, QuickScanFinding } from '@/services/questionsService';
import { questionsService } from '@/services/questionsService';
import { queryKeys } from '@/lib/queryKeys';

interface QuestionsContextType {
  /** Server-persisted questions (with embedded vulnerabilities). */
  questions: QuestionOut[];
  setQuestions: React.Dispatch<React.SetStateAction<QuestionOut[]>>;
  /** Ephemeral quick-scan results keyed by question_id. */
  quickScanResults: Record<number, QuickScanFinding>;
  setQuickScanResults: React.Dispatch<React.SetStateAction<Record<number, QuickScanFinding>>>;
  /** Current project's permission from ThreatCat. */
  permission: string | null;
  setPermission: (p: string | null) => void;
  /** The project ID currently loaded. */
  projectId: number | null;
  /** Load questions for a project from the API. */
  loadQuestions: (projectId: number) => Promise<void>;
  /** Replace a single question in the local list (after server update). */
  replaceQuestion: (updated: QuestionOut) => void;
  /** Remove a question from the local list. */
  removeQuestion: (id: number) => void;
  /** Invalidate the questions cache, triggering a background refetch. */
  invalidateQuestions: () => Promise<void>;
  isGeneratingQuestions: boolean;
  setIsGeneratingQuestions: (val: boolean) => void;
  isAnalyzingQuestionnaire: boolean;
  setIsAnalyzingQuestionnaire: (val: boolean) => void;
  analyzingQuestionIds: Set<number>;
  setAnalyzingQuestionIds: (updater: (prev: Set<number>) => Set<number>) => void;
  isLoading: boolean;
  resetQuestions: () => void;
}

const QuestionsContext = createContext<QuestionsContextType | undefined>(undefined);

export function QuestionsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState<number | null>(null);
  const [quickScanResults, setQuickScanResults] = useState<Record<number, QuickScanFinding>>({});
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [isAnalyzingQuestionnaire, setIsAnalyzingQuestionnaire] = useState(false);
  const [analyzingQuestionIds, setAnalyzingQuestionIds] = useState<Set<number>>(new Set());

  const { data, isLoading } = useQuery<QuestionsListResponse>({
    queryKey: queryKeys.questions.all(projectId!),
    queryFn: () => questionsService.getQuestions(projectId!),
    enabled: projectId != null,
  });

  const questions = data?.questions ?? [];
  const permission = data?.permission ?? null;

  const setQuestions: React.Dispatch<React.SetStateAction<QuestionOut[]>> = useCallback(
    (updater) => {
      if (projectId == null) return;
      queryClient.setQueryData<QuestionsListResponse>(
        queryKeys.questions.all(projectId),
        (old) => {
          if (!old) return old;
          const newQuestions = typeof updater === 'function' ? updater(old.questions) : updater;
          return { ...old, questions: newQuestions };
        },
      );
    },
    [projectId, queryClient],
  );

  const setPermission = useCallback(
    (p: string | null) => {
      if (projectId == null) return;
      queryClient.setQueryData<QuestionsListResponse>(
        queryKeys.questions.all(projectId),
        (old) => (old ? { ...old, permission: p } : old),
      );
    },
    [projectId, queryClient],
  );

  const loadQuestions = useCallback(async (pid: number) => {
    setProjectId(pid);
    setQuickScanResults({});
  }, []);

  const replaceQuestion = useCallback((updated: QuestionOut) => {
    setQuestions((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
  }, [setQuestions]);

  const removeQuestion = useCallback((id: number) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  }, [setQuestions]);

  const invalidateQuestions = useCallback(async () => {
    if (projectId == null) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.questions.all(projectId) });
  }, [projectId, queryClient]);

  const resetQuestions = useCallback(() => {
    if (projectId != null) {
      queryClient.removeQueries({ queryKey: queryKeys.questions.all(projectId) });
    }
    setQuickScanResults({});
    setProjectId(null);
    setIsGeneratingQuestions(false);
    setIsAnalyzingQuestionnaire(false);
    setAnalyzingQuestionIds((_prev) => new Set());
  }, [projectId, queryClient]);

  const value: QuestionsContextType = {
    questions,
    setQuestions,
    quickScanResults,
    setQuickScanResults,
    permission,
    setPermission,
    projectId,
    loadQuestions,
    replaceQuestion,
    removeQuestion,
    invalidateQuestions,
    isGeneratingQuestions,
    setIsGeneratingQuestions,
    isAnalyzingQuestionnaire,
    setIsAnalyzingQuestionnaire,
    analyzingQuestionIds,
    setAnalyzingQuestionIds,
    isLoading,
    resetQuestions,
  };

  return <QuestionsContext.Provider value={value}>{children}</QuestionsContext.Provider>;
}

export function useQuestions() {
  const context = useContext(QuestionsContext);
  if (!context) {
    throw new Error('useQuestions must be used within QuestionsProvider');
  }
  return context;
}
